import { Platform } from 'react-native';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import {
    Cow,
    CowRecord,
    DairyRecord,
    DashboardStats,
    ReportStats,
    User,
    AuditLog,
    Customer,
    CustomerTransaction,
    DairyPurchase,
    CustomerType,
    Employee,
    SalaryTransaction,
    EmployeeSalarySummary,
    MonthlySalaryRecord,
} from '../models/types';
import { baseUrl, webBaseUrl } from '../constants/CommonConstants';
import { storage, STORAGE_KEYS } from './Storage';
import AnalyticsService from './AnalyticsService';

// API Base URL - adjust for different environments
const getBaseUrl = (): string => {
    if (Platform.OS === 'web') {
        // For web, use relative URL or environment variable
        // Using specific IP address as requested
        return webBaseUrl;
    }
    // For mobile, use your server IP or domain
    return baseUrl; // Android emulator
};

const API_BASE_URL = getBaseUrl();

const getEndpointName = (url?: string): string => {
    if (!url) return 'Unknown';
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/').filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : 'Unknown';
};

// Custom error class for API errors
export class ApiError extends Error {
    constructor(
        public message: string,
        public statusCode?: number,
        public data?: any
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

class ApiService {
    private token: string | null = null;
    private user: User | null = null;
    private axiosInstance: AxiosInstance;

    constructor() {
        // Create axios instance with default configuration
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000, // 30 seconds
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Setup interceptors
        this.setupInterceptors();
    }

    // Setup request and response interceptors
    private setupInterceptors(): void {
        // Request interceptor - attach token to all requests
        this.axiosInstance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                if (this.token && config.headers) {
                    config.headers.Authorization = `Bearer ${this.token}`;
                }

                // Log request in development
                if (__DEV__ && Platform.OS !== 'web') {
                    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
                }

                AnalyticsService.track(`API Request: ${getEndpointName(config.url)}`, {
                    method: config.method?.toUpperCase(),
                    url: config.url,
                    data: config.data,
                    params: config.params,
                });

                return config;
            },
            (error) => {
                console.error('[API Request Error]', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle errors globally
        this.axiosInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                // Log response in development
                if (__DEV__ && Platform.OS !== 'web') {
                    console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
                }

                AnalyticsService.track(`API Response: ${getEndpointName(response.config.url)}`, {
                    method: response.config.method?.toUpperCase(),
                    url: response.config.url,
                    status: response.status,
                    data: response.data,
                });

                // Axios automatically parses JSON, return the data directly
                return response;
            },
            async (error: AxiosError) => {
                // Handle different error scenarios
                if (error.response) {
                    // Server responded with error status
                    const statusCode = error.response.status;
                    const data = error.response.data as any;

                    console.error(`[API Error] ${statusCode}:`, data);

                    AnalyticsService.track(`API Error: ${getEndpointName(error.response?.config?.url)}`, {
                        method: error.response?.config?.method?.toUpperCase(),
                        url: error.response?.config?.url,
                        status: statusCode,
                        message: data?.message || error.message,
                        responseData: data,
                    });

                    // Handle specific status codes
                    switch (statusCode) {
                        case 401:
                            // Unauthorized - could trigger logout
                            console.warn('Unauthorized access - token may be expired');
                            break;
                        case 403:
                            console.warn('Forbidden - insufficient permissions');
                            break;
                        case 404:
                            console.warn('Resource not found');
                            break;
                        case 500:
                            console.error('Server error');
                            break;
                    }

                    throw new ApiError(
                        data?.message || error.message || 'Request failed',
                        statusCode,
                        data
                    );
                } else if (error.request) {
                    // Request made but no response received (network error)
                    console.error('[API Network Error]', error.message);
                    throw new ApiError(
                        'Network error - please check your connection',
                        undefined,
                        error
                    );
                } else {
                    // Something else happened
                    console.error('[API Error]', error.message);
                    throw new ApiError(error.message);
                }
            }
        );
    }

    // Initialize the service - load token from storage
    async init(): Promise<void> {
        try {
            // Load token for active session
            const token = storage.getString(STORAGE_KEYS.TOKEN);
            const userStr = storage.getString(STORAGE_KEYS.USER);

            if (token) {
                this.token = token;
            }
            if (userStr) {
                this.user = JSON.parse(userStr);
            }
        } catch (error) {
            console.error('Error initializing ApiService:', error);
        }
    }

    // =============== AUTH ===============

    async login(username: string, password: string): Promise<{ user: User; token: string }> {
        const response = await this.axiosInstance.post<{ success: boolean; data: { user: any; token: string } }>(
            '/api/auth/login',
            { username, password }
        );

        const { user, token } = response.data.data;
        this.token = token;
        this.user = user;

        storage.set(STORAGE_KEYS.TOKEN, token);
        storage.set(STORAGE_KEYS.USER, JSON.stringify(user));

        return { user, token };
    }

    async logout(): Promise<void> {
        this.token = null;
        this.user = null;
        storage.delete(STORAGE_KEYS.TOKEN);
        storage.delete(STORAGE_KEYS.USER);
    }

    async getCurrentUser(): Promise<User | null> {
        return this.user;
    }

    isAuthenticated(): boolean {
        return !!this.token;
    }

    async updatePassword(newPassword: string): Promise<void> {
        await this.axiosInstance.post('/api/auth/change-password', { newPassword });
    }

    // OTP-based password change
    async sendChangePasswordOTP(): Promise<{ phoneNumber?: string }> {
        const response = await this.axiosInstance.post<{ success: boolean; data?: { phoneNumber: string } }>(
            '/api/auth/send-change-password-otp'
        );
        return response.data.data || {};
    }

    async verifyChangePasswordOTP(oldPassword: string, newPassword: string, otp: string): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message?: string }>(
                '/api/auth/verify-change-password-otp',
                { oldPassword, newPassword, otp }
            );
            return { success: true, message: response.data.message };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    }

    // OTP-based forgot password
    async sendForgotPasswordOTP(username: string): Promise<{ success: boolean; phoneNumber?: string; message?: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; data?: { phoneNumber: string }; message?: string }>(
                '/api/auth/send-forgot-password-otp',
                { username }
            );
            return { success: true, phoneNumber: response.data.data?.phoneNumber, message: response.data.message };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    }

    async verifyForgotPasswordOTP(username: string, otp: string): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message?: string }>(
                '/api/auth/verify-forgot-password-otp',
                { username, otp }
            );
            return { success: true, message: response.data.message };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    }

    async resetPasswordWithOTP(username: string, otp: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message?: string }>(
                '/api/auth/reset-password',
                { username, otp, newPassword }
            );
            return { success: true, message: response.data.message };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    }

    // =============== COWS ===============

    async getCows(status?: 'active' | 'sold' | 'deceased'): Promise<Cow[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: Cow[] }>('/api/cows', {
            params: status ? { status } : undefined
        });
        return response.data.data;
    }

    async getCow(cowId: string): Promise<Cow | null> {
        try {
            const response = await this.axiosInstance.get<{ success: boolean; data: Cow }>(`/api/cows/${cowId}`);
            return response.data.data;
        } catch {
            return null;
        }
    }

    async addCow(cow: Omit<Cow, 'id' | 'createdAt' | 'updatedAt'>): Promise<Cow> {
        const response = await this.axiosInstance.post<{ success: boolean; data: Cow }>('/api/cows', cow);
        return response.data.data;
    }

    async updateCow(cowId: string, cow: Partial<Cow>): Promise<Cow> {
        const response = await this.axiosInstance.put<{ success: boolean; data: Cow }>(`/api/cows/${cowId}`, cow);
        return response.data.data;
    }

    async deleteCow(cowId: string): Promise<void> {
        await this.axiosInstance.delete(`/api/cows/${cowId}`);
    }

    async getNextCowId(): Promise<string> {
        const response = await this.axiosInstance.get<{ success: boolean; data: { nextId: string } }>('/api/cows/next-id');
        return response.data.data.nextId;
    }

    async getPregnantCows(): Promise<Cow[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: Cow[] }>('/api/cows', {
            params: { pregnant: 'true' }
        });
        return response.data.data;
    }

    // =============== COW RECORDS ===============

    async getCowRecords(cowId: string, recordType?: string): Promise<CowRecord[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: CowRecord[] }>(
            `/api/cow-records/cow/${cowId}`,
            { params: recordType ? { recordType } : undefined }
        );
        return response.data.data;
    }

    async addCowRecord(record: Omit<CowRecord, 'id' | 'createdAt'>): Promise<CowRecord> {
        const response = await this.axiosInstance.post<{ success: boolean; data: CowRecord }>('/api/cow-records', record);
        return response.data.data;
    }

    async updateCowRecord(recordId: string, record: Partial<CowRecord>): Promise<CowRecord> {
        const response = await this.axiosInstance.put<{ success: boolean; data: CowRecord }>(`/api/cow-records/${recordId}`, record);
        return response.data.data;
    }

    async deleteCowRecord(recordId: string): Promise<any> {
        const response = await this.axiosInstance.delete<any>(`/api/cow-records/${recordId}`);
        return response.data.data;

    }

    // =============== DAIRY RECORDS ===============

    async getDairyRecords(recordType?: string, includeDeleted: boolean = false): Promise<DairyRecord[]> {
        const params: any = {};
        if (recordType) params.recordType = recordType;
        if (includeDeleted) params.includeDeleted = 'true';

        const response = await this.axiosInstance.get<{ success: boolean; data: DairyRecord[] }>(
            '/api/dairy-records',
            { params: Object.keys(params).length > 0 ? params : undefined }
        );
        return response.data.data;
    }

    async addDairyRecord(record: Omit<DairyRecord, 'id' | 'createdAt'>): Promise<DairyRecord> {
        const response = await this.axiosInstance.post<{ success: boolean; data: DairyRecord }>('/api/dairy-records', record);
        return response.data.data;
    }

    async updateDairyRecord(recordId: string, record: Partial<DairyRecord>): Promise<DairyRecord> {
        const response = await this.axiosInstance.put<{ success: boolean; data: DairyRecord }>(`/api/dairy-records/${recordId}`, record);
        return response.data.data;
    }

    async deleteDairyRecord(recordId: string): Promise<any> {
        const response = await this.axiosInstance.delete<any>(`/api/dairy-records/${recordId}`);
        return response;
    }

    // =============== CUSTOMERS ===============

    async getCustomers(customerType?: CustomerType): Promise<Customer[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: Customer[] }>('/api/customers', {
            params: customerType ? { customerType } : undefined
        });
        return response.data.data;
    }

    async getCustomerById(customerId: string): Promise<Customer | null> {
        try {
            const response = await this.axiosInstance.get<{ success: boolean; data: Customer }>(`/api/customers/${customerId}`);
            return response.data.data;
        } catch {
            return null;
        }
    }

    async addCustomer(customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>): Promise<Customer> {
        const response = await this.axiosInstance.post<{ success: boolean; data: Customer }>('/api/customers', customer);
        return response.data.data;
    }

    async updateCustomer(customerId: string, customer: Partial<Customer>): Promise<Customer> {
        const response = await this.axiosInstance.put<{ success: boolean; data: Customer }>(`/api/customers/${customerId}`, customer);
        return response.data.data;
    }

    async deleteCustomer(customerId: string): Promise<void> {
        await this.axiosInstance.delete(`/api/customers/${customerId}`);
    }

    async getCustomerBalance(customerId: string): Promise<{
        totalCredit: number;
        totalPaid: number;
        balance: number;
        advance: number;
    }> {
        const response = await this.axiosInstance.get<{
            success: boolean;
            data: { totalCredit: number; totalPaid: number; balance: number; advance: number };
        }>(`/api/customers/${customerId}/balance`);
        return response.data.data;
    }

    async getCustomerDairyRecords(customerId: string, fromDate?: string, toDate?: string): Promise<DairyRecord[]> {
        const params: any = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        const response = await this.axiosInstance.get<{ success: boolean; data: DairyRecord[] }>(
            `/api/customers/${customerId}/dairy-records`,
            { params: Object.keys(params).length > 0 ? params : undefined }
        );
        return response.data.data;
    }

    async getCustomerTransactions(customerId: string): Promise<CustomerTransaction[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: CustomerTransaction[] }>(
            `/api/customers/${customerId}/transactions`
        );
        return response.data.data;
    }

    async addCustomerTransaction(
        customerId: string,
        transaction: Omit<CustomerTransaction, 'id' | 'customerId' | 'createdAt'>
    ): Promise<CustomerTransaction> {
        const response = await this.axiosInstance.post<{ success: boolean; data: CustomerTransaction }>(
            `/api/customers/${customerId}/transactions`,
            transaction
        );
        return response.data.data;
    }

    // =============== DAIRY PURCHASES ===============

    async getDairyPurchases(fromDate?: string, toDate?: string): Promise<DairyPurchase[]> {
        const params: any = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        const response = await this.axiosInstance.get<{ success: boolean; data: DairyPurchase[] }>(
            '/api/dairy-purchases',
            { params: Object.keys(params).length > 0 ? params : undefined }
        );
        return response.data.data;
    }

    async addDairyPurchase(purchase: Omit<DairyPurchase, 'id' | 'createdAt' | 'updatedAt'>): Promise<DairyPurchase> {
        const response = await this.axiosInstance.post<{ success: boolean; data: DairyPurchase }>('/api/dairy-purchases', purchase);
        return response.data.data;
    }

    async updateDairyPurchase(purchaseId: string, purchase: Partial<DairyPurchase>): Promise<DairyPurchase> {
        const response = await this.axiosInstance.put<{ success: boolean; data: DairyPurchase }>(
            `/api/dairy-purchases/${purchaseId}`,
            purchase
        );
        return response.data.data;
    }

    async deleteDairyPurchase(purchaseId: string): Promise<void> {
        await this.axiosInstance.delete(`/api/dairy-purchases/${purchaseId}`);
    }

    async getDairyPurchasesTotal(fromDate?: string, toDate?: string): Promise<number> {
        const params: any = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        const response = await this.axiosInstance.get<{ success: boolean; data: { total: number } }>(
            '/api/dairy-purchases/total',
            { params: Object.keys(params).length > 0 ? params : undefined }
        );
        return response.data.data.total;
    }

    // =============== DASHBOARD & REPORTS ===============

    async getDashboardStats(): Promise<DashboardStats> {
        const response = await this.axiosInstance.get<{ success: boolean; data: DashboardStats }>('/api/dashboard/stats');
        return response.data.data;
    }

    async getReportStats(fromDate: string, toDate: string): Promise<ReportStats> {
        const response = await this.axiosInstance.get<{ success: boolean; data: ReportStats }>(
            '/api/dashboard/reports',
            { params: { fromDate, toDate } }
        );
        return response.data.data;
    }

    // =============== EMPLOYEES ===============

    async getEmployees(): Promise<Employee[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: Employee[] }>('/api/employees');
        return response.data.data;
    }

    async getEmployeeById(employeeId: string): Promise<Employee | null> {
        try {
            const response = await this.axiosInstance.get<{ success: boolean; data: Employee }>(`/api/employees/${employeeId}`);
            return response.data.data;
        } catch {
            return null;
        }
    }

    async addEmployee(employee: Omit<Employee, 'id' | 'createdAt' | 'updatedAt'>): Promise<Employee> {
        const response = await this.axiosInstance.post<{ success: boolean; data: Employee }>('/api/employees', employee);
        return response.data.data;
    }

    async updateEmployee(employeeId: string, employee: Partial<Employee>): Promise<Employee> {
        const response = await this.axiosInstance.put<{ success: boolean; data: Employee }>(`/api/employees/${employeeId}`, employee);
        return response.data.data;
    }

    async deleteEmployee(employeeId: string): Promise<void> {
        await this.axiosInstance.delete(`/api/employees/${employeeId}`);
    }

    async getEmployeeSalarySummary(employeeId: string): Promise<EmployeeSalarySummary> {
        const response = await this.axiosInstance.get<{ success: boolean; data: EmployeeSalarySummary }>(
            `/api/employees/${employeeId}/salary-summary`
        );
        return response.data.data;
    }

    async getSalaryTransactions(employeeId: string): Promise<SalaryTransaction[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: SalaryTransaction[] }>(
            `/api/employees/${employeeId}/salary-transactions`
        );
        return response.data.data;
    }

    async addSalaryTransaction(
        employeeId: string,
        transaction: { month: string; paidAmount: number; date: string; notes?: string }
    ): Promise<SalaryTransaction> {
        const response = await this.axiosInstance.post<{ success: boolean; data: SalaryTransaction }>(
            `/api/employees/${employeeId}/salary-transactions`,
            transaction
        );
        return response.data.data;
    }

    async updateSalaryTransaction(
        employeeId: string,
        transactionId: string,
        transaction: { paidAmount?: number; date?: string; notes?: string }
    ): Promise<SalaryTransaction> {
        const response = await this.axiosInstance.put<{ success: boolean; data: SalaryTransaction }>(
            `/api/employees/${employeeId}/salary-transactions/${transactionId}`,
            transaction
        );
        return response.data.data;
    }

    async deleteSalaryTransaction(employeeId: string, transactionId: string): Promise<void> {
        await this.axiosInstance.delete(`/api/employees/${employeeId}/salary-transactions/${transactionId}`);
    }

    async getMonthlySalarySummary(employeeId: string): Promise<MonthlySalaryRecord[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: MonthlySalaryRecord[] }>(
            `/api/employees/${employeeId}/monthly-summary`
        );
        return response.data.data;
    }

    // =============== AUDIT LOGS ===============

    async getAuditLogs(limit: number = 100): Promise<AuditLog[]> {
        const response = await this.axiosInstance.get<{ success: boolean; data: AuditLog[] }>(
            '/api/audit-logs',
            { params: { limit } }
        );
        return response.data.data;
    }
}

export default new ApiService();
